import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Grid from '@mui/material/Grid';
import Toolbar from '@mui/material/Toolbar';
import { useTheme } from '@mui/material/styles';
import UserSettings from 'components/Layouts/UserSettings';
import { LogoUrl, MixpanelEventName } from 'utils/constants';
import { FetchContext, useHttp } from 'hooks/use-fetch';
import Chip from '@mui/material/Chip';
import { Link, useNavigate } from 'react-router-dom';
import { ProviderPractice } from 'pages/SuperAdmin/types';
import Tooltip from '@mui/material/Tooltip';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Badge, Box, IconButton, keyframes, useMediaQuery } from '@mui/material';
import NotificationsDrawer from 'pages/Dashboard/NotificationsDrawer';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { useQuery } from 'react-query';
import { healthRecordRefreshUrls } from 'utils/apiUrls';
import { unreadNotificationsCount } from 'pages/Dashboard/services/healthrecords.services';
import { trackMixpanelEvent } from 'utils/utilMethods';
import InlineNavItems from 'components/Layouts/HeaderNav';
import * as spaUrl from 'utils/spaUrls';
import { UserRole } from 'types/NavTreeModel';
import DemoModeBanner from 'components/Common/DemoBanner';
import { PersonAdd } from '@mui/icons-material';
import { mainNav } from 'utils/spaUrls';
import HLButton from 'components/HLButton';
import { grey } from 'theme';

const bounceKeyframe = keyframes`
  0% {
    transform: rotate(0deg);
  }
  15% {
    transform: rotate(10deg);
  }
  30% {
    transform: rotate(-10deg);
  }
  45% {
    transform: rotate(5deg);
  }
  60% {
    transform: rotate(-5deg);
  }
  100% {
    transform: rotate(0deg);
  }
`;

const REFRESH_INTERVAL = 5000;
const REFRESH_DURATION = 2000;

export default function Header() {
  const theme = useTheme();
  const navigator = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    activeProvider,
    superAdminId,
    updateActiveProvider,
    userRoles,
    isDemoUser,
  } = React.useContext(FetchContext);
  const [showNotificationsDrawer, setShowNotificationsDrawer] = React.useState(false);
  const { http, providerId, isHrRefreshEnabled, providerName, selectedProviderName } = useHttp();
  const [refreshing, setRefreshing] = React.useState(false);
  React.useEffect(() => {
    const interval = setInterval(() => {
      setRefreshing(true);
      setTimeout(() => setRefreshing(false), REFRESH_DURATION);
    }, REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  const isChiefAdmin = !!userRoles[UserRole.CHIEF_ADMIN];
  const isSuperAdmin = !!superAdminId;

  const shouldFetchNotifications = !!providerId && isHrRefreshEnabled;

  const notificationsCount = useQuery({
    queryKey: [healthRecordRefreshUrls.unreadNotifications.queryUrl, providerId],
    queryFn: unreadNotificationsCount(http.get),
    enabled: shouldFetchNotifications,
    refetchInterval: shouldFetchNotifications ? 120000 : false,
  });

  React.useEffect(() => {
    if (shouldFetchNotifications) {
      const interval = setInterval(() => {
        notificationsCount.refetch();
      }, 120000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [notificationsCount, shouldFetchNotifications]);

  const handleActiveProviderClick = () => {
    updateActiveProvider({ providerId: superAdminId } as ProviderPractice);
    navigator('/providers-list');
  };

  const openNotificationDrawer = () => {
    setShowNotificationsDrawer(true);
    trackMixpanelEvent(MixpanelEventName.HEADER_NOTIFICATION_BELL_CLICKED, {
      'unread count': (notificationsCount.data?.notifications.unread ?? 0).toString(),
      'provider id': providerId ?? '',
      'logged in provider name': providerName ?? '',
      'selected provider name': selectedProviderName ?? '',
    });
  };

  const toggleRecordRefreshDrawer = (open: boolean) => {
    setShowNotificationsDrawer(open);
  };

  const headerHeight = `calc(${theme.custom.appBarHeight} + ${!isChiefAdmin ? theme.custom.navBarHeight : '0px'} + ${
    isDemoUser ? theme.custom.demoBarHeight : '0px'
  })`;

  return (
    <>
      <NotificationsDrawer
        isOpen={showNotificationsDrawer}
        toggleDrawer={toggleRecordRefreshDrawer}
      />
      <AppBar
        position={isMobile ? 'static' : 'fixed'}
        sx={{
          height: headerHeight,
          justifyContent: 'center',
          background: theme.palette.background.default,
          boxShadow: '0 2px 4px 0 #EAEAEA',
        }}
      >
        {isDemoUser ? <DemoModeBanner /> : null}
        <Toolbar
          disableGutters
          sx={{
            height: headerHeight,
            borderBottom: '1px solid #E2E3E4',
          }}
        >
          <Box width='100%'>
            <Box
              sx={{
                maxWidth: '960px',
                margin: '0 auto',
                px: { xs: 1, md: 1.5 },
              }}
            >
              <Grid container justifyContent='space-between'>
                <Grid item display='flex' alignItems='center'>
                  <Box component={Link} to={spaUrl.mainNav.dashboard} display='flex' alignItems='center'>
                    <Box
                      component='img'
                      src={LogoUrl.lg}
                      alt='Logo'
                      height={theme.custom.logoHeight}
                      width={theme.custom.logoWidth}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Box>
                </Grid>
                <Grid item display='flex' alignItems='center'>
                  {isSuperAdmin && activeProvider?.providerName && (
                    <Tooltip title='Change active provider'>
                      <Chip
                        sx={{
                          mr: 3,
                        }}
                        color='primary'
                        label={(
                          <div>
                            <b>Active provider :</b>
                            {' '}
                            {activeProvider.providerName.replace('%', ' ')}
                          </div>
                        )}
                        variant='outlined'
                        onClick={handleActiveProviderClick}
                      />
                    </Tooltip>
                  )}
                  <Grid item display='flex' alignItems='center' gap={4.5}>
                    {!isChiefAdmin && !isSuperAdmin && !isMobile && (
                      <HLButton
                        color='primary'
                        variant='contained'
                        size='large'
                        type='submit'
                        startIcon={<PersonAdd />}
                        onClick={() => {
                          navigator(mainNav.addNewPatient);
                          trackMixpanelEvent(
                            MixpanelEventName.DASHBOARD_ADD_PATIENTS_BUTTON_CLICKED,
                            {},
                          );
                        }}
                      >
                        ADD PATIENTS
                      </HLButton>
                    )}
                    {!isChiefAdmin && shouldFetchNotifications
                      && !notificationsCount.isLoading
                      && notificationsCount.data
                      && ((notificationsCount?.data?.notifications?.unread as number) > 0 ? (
                        <IconButton sx={{ p: 0.5 }} onClick={openNotificationDrawer}>
                          <Badge
                            badgeContent={notificationsCount.data?.notifications.unread}
                            color='error'
                            showZero={false}
                            sx={{
                              '& .MuiBadge-badge': {
                                marginTop: '9px',
                                marginRight: '9px',
                              },
                            }}
                          >
                            <NotificationsIcon
                              sx={{
                                color: '#0000008A',
                                animation: refreshing
                                  ? `${bounceKeyframe} 1s ease-in-out infinite`
                                  : 'none',
                              }}
                              fontSize='large'
                            />
                          </Badge>
                        </IconButton>
                      ) : (
                        <IconButton sx={{ p: 0.5 }} onClick={openNotificationDrawer}>
                          <NotificationsNoneOutlinedIcon
                            sx={{ color: '#0000008A' }}
                            fontSize='medium'
                          />
                        </IconButton>
                      ))}
                    <UserSettings />
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Toolbar>
        {!isChiefAdmin && (
        <Box
          display='flex'
          alignItems='center'
          justifyContent='center'
          minHeight={theme.custom.navBarHeight}
          borderBottom={`1px solid ${grey[100]}`}
        >
          <Box maxWidth={960} width='100%' gap={1.5} display='flex' alignItems='flex-end' justifyContent='flex-start'>
            <InlineNavItems />
          </Box>
        </Box>
        )}
      </AppBar>
    </>
  );
}
